import { ArrowLeft, Search } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

import { TYPES, TYPE_META, fmt, isActive, isReady } from '../logic';

import type {
  ProductCompletenessRow,
  ProductsBreakdownResponse,
  ProductType,
} from '@utazzo/shared';

/**
 * Products dashboard - "by geography" view. Takes the same six row arrays the
 * by-product view uses and regroups them by country (all six types) or by city
 * (the four city-scoped types: activities, transfers, hotels, cruises - visas
 * and insurance are country-only). Each cell shows ready / active for that type
 * in that place; clicking a number (or a place) opens a detail list of those
 * products, filterable by ready / not ready, each deep-linked to its editor.
 *
 * "Not ready" here is the honest per-place readiness (active and not ready),
 * including long-haul - unlike the headline KPIs, which park long-haul as
 * "skipped". A place's detail header still calls that out.
 */

type GeoLevel = 'country' | 'city';
type ListFilter = 'all' | 'ready' | 'notready' | 'skipped';

// City breakdown only makes sense for the city-scoped masters.
const CITY_TYPES: ProductType[] = ['activities', 'transfers', 'hotels', 'cruises'];

// Amber (long-haul "skipped") and red (short-haul "not ready") tokens.
const SKIP_BG = '#fef3c7';
const SKIP_FG = '#b45309';
const RED_BG = '#fde8ec';
const RED_FG = '#be123c';

interface Cell {
  active: number;
  ready: number;
  /** short-haul, not ready - a real gap (red). */
  blocked: number;
  /** long-haul, not ready - parked (yellow), not an error. */
  skipped: number;
}
interface GeoRow {
  id: string;
  name: string;
  sub: string | null;
  cells: Record<ProductType, Cell>;
  totActive: number;
  totReady: number;
  totBlocked: number;
  totSkipped: number;
}

const emptyCell = (): Cell => ({ active: 0, ready: 0, blocked: 0, skipped: 0 });

const geoKey = (r: ProductCompletenessRow, level: GeoLevel): string | null =>
  level === 'city' ? r.cityId : r.countryId;
const geoName = (r: ProductCompletenessRow, level: GeoLevel): string =>
  (level === 'city' ? r.cityName : r.countryName) ?? 'Unspecified';

const Chip = ({ bg, fg, children }: { bg: string; fg: string; children: React.ReactNode }) => (
  <span className="pd-tag" style={{ background: bg, color: fg }}>
    {children}
  </span>
);

export const ProductsGeographyView = ({
  data,
}: {
  data: ProductsBreakdownResponse;
}): React.JSX.Element => {
  const [level, setLevel] = React.useState<GeoLevel>('country');
  const [sort, setSort] = React.useState<'gaps' | 'size' | 'name'>('gaps');
  const [search, setSearch] = React.useState('');
  const [detail, setDetail] = React.useState<{
    id: string;
    name: string;
    sub: string | null;
    type: ProductType;
    filter: ListFilter;
  } | null>(null);

  const colTypes = level === 'city' ? CITY_TYPES : TYPES;

  // Reset any open detail when the grouping level flips.
  React.useEffect(() => setDetail(null), [level]);

  const rows = React.useMemo<GeoRow[]>(() => {
    const map = new Map<string, GeoRow>();
    for (const type of colTypes) {
      for (const r of data[type]) {
        const id = geoKey(r, level);
        if (!id) continue;
        let gr = map.get(id);
        if (!gr) {
          gr = {
            id,
            name: geoName(r, level),
            sub: level === 'city' ? r.countryName : null,
            cells: Object.fromEntries(colTypes.map((t) => [t, emptyCell()])) as Record<
              ProductType,
              Cell
            >,
            totActive: 0,
            totReady: 0,
            totBlocked: 0,
            totSkipped: 0,
          };
          map.set(id, gr);
        }
        if (!isActive(r)) continue;
        const cell = gr.cells[type];
        cell.active += 1;
        gr.totActive += 1;
        if (isReady(r, type)) {
          cell.ready += 1;
          gr.totReady += 1;
        } else if (r.isLongHaul) {
          cell.skipped += 1;
          gr.totSkipped += 1;
        } else {
          cell.blocked += 1;
          gr.totBlocked += 1;
        }
      }
    }
    return [...map.values()];
  }, [data, level, colTypes]);

  const term = search.trim().toLowerCase();
  const visible = React.useMemo(() => {
    const filtered = term
      ? rows.filter(
          (g) => g.name.toLowerCase().includes(term) || (g.sub ?? '').toLowerCase().includes(term),
        )
      : rows;
    const sorted = [...filtered];
    if (sort === 'gaps')
      sorted.sort(
        (a, b) =>
          b.totBlocked - a.totBlocked || b.totSkipped - a.totSkipped || b.totActive - a.totActive,
      );
    else if (sort === 'size') sorted.sort((a, b) => b.totActive - a.totActive);
    else sorted.sort((a, b) => a.name.localeCompare(b.name));
    return sorted;
  }, [rows, term, sort]);

  const openDetail = (g: GeoRow, type: ProductType) => {
    const c = g.cells[type];
    setDetail({
      id: g.id,
      name: g.name,
      sub: g.sub,
      type,
      filter: c.blocked > 0 ? 'notready' : c.skipped > 0 ? 'skipped' : 'all',
    });
  };

  if (detail) {
    return (
      <GeoDetail
        data={data}
        level={level}
        detail={detail}
        colTypes={colTypes}
        onType={(type) => setDetail({ ...detail, type, filter: 'all' })}
        onFilter={(filter) => setDetail({ ...detail, filter })}
        onBack={() => setDetail(null)}
      />
    );
  }

  return (
    <div style={{ marginTop: 16 }}>
      {/* Controls */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className={`pd-pill ${level === 'country' ? 'on' : ''}`}
            onClick={() => setLevel('country')}
          >
            Countries
          </button>
          <button
            type="button"
            className={`pd-pill ${level === 'city' ? 'on' : ''}`}
            onClick={() => setLevel('city')}
          >
            Cities
          </button>
          <span style={{ width: 1, height: 18, background: '#e3e5e5', margin: '0 4px' }} />
          <span style={{ fontSize: 12, color: '#6b7075' }}>Sort:</span>
          {(
            [
              ['gaps', 'Most gaps'],
              ['size', 'Most products'],
              ['name', 'A-Z'],
            ] as const
          ).map(([k, lbl]) => (
            <button
              key={k}
              type="button"
              className={`pd-pill ${sort === k ? 'on' : ''}`}
              onClick={() => setSort(k)}
            >
              {lbl}
            </button>
          ))}
        </div>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 9, top: 9, color: '#9aa0a4' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${level === 'city' ? 'cities' : 'countries'}…`}
            style={{
              fontSize: 13,
              padding: '6px 10px 6px 28px',
              borderRadius: 8,
              border: '1px solid #e3e5e5',
              minWidth: 200,
            }}
          />
        </div>
      </div>

      <div className="pd-card" style={{ overflow: 'hidden' }}>
        <div style={{ maxHeight: 560, overflow: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>{level === 'city' ? 'City' : 'Country'}</th>
                {colTypes.map((t) => (
                  <th key={t}>{TYPE_META[t].label}</th>
                ))}
                <th>Gaps</th>
              </tr>
            </thead>
            <tbody>
              {visible.slice(0, 400).map((g) => (
                <tr key={g.id}>
                  <td>
                    <button
                      type="button"
                      onClick={() =>
                        openDetail(
                          g,
                          colTypes.find((t) => g.cells[t].active > 0) ??
                            colTypes[0] ??
                            'activities',
                        )
                      }
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        textAlign: 'left',
                        font: 'inherit',
                      }}
                    >
                      <div style={{ fontWeight: 600, color: '#113a4a' }}>{g.name}</div>
                      {g.sub && <div style={{ fontSize: 11, color: '#8a9095' }}>{g.sub}</div>}
                    </button>
                  </td>
                  {colTypes.map((t) => {
                    const c = g.cells[t];
                    return (
                      <td key={t}>
                        {c.active === 0 ? (
                          <span style={{ color: '#c5c9cb' }}>&ndash;</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openDetail(g, t)}
                            title={`${c.ready} ready / ${c.active} published${c.blocked ? ` - ${c.blocked} not ready` : ''}${c.skipped ? ` - ${c.skipped} long-haul skipped` : ''}`}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              font: 'inherit',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                              <b style={{ color: c.blocked ? RED_FG : '#047857' }}>
                                {fmt(c.ready)}
                              </b>
                              <span style={{ color: '#9aa0a4' }}>/{fmt(c.active)}</span>
                            </span>
                            {c.blocked > 0 && (
                              <Chip bg={RED_BG} fg={RED_FG}>
                                {fmt(c.blocked)}
                              </Chip>
                            )}
                            {c.skipped > 0 && (
                              <Chip bg={SKIP_BG} fg={SKIP_FG}>
                                {fmt(c.skipped)}
                              </Chip>
                            )}
                          </button>
                        )}
                      </td>
                    );
                  })}
                  <td>
                    {g.totBlocked === 0 && g.totSkipped === 0 ? (
                      <Chip bg="#e7f6ef" fg="#047857">
                        All ready
                      </Chip>
                    ) : (
                      <span style={{ display: 'inline-flex', gap: 4, justifyContent: 'center' }}>
                        {g.totBlocked > 0 && (
                          <Chip bg={RED_BG} fg={RED_FG}>
                            {fmt(g.totBlocked)}
                          </Chip>
                        )}
                        {g.totSkipped > 0 && (
                          <Chip bg={SKIP_BG} fg={SKIP_FG}>
                            {fmt(g.totSkipped)}
                          </Chip>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td
                    colSpan={colTypes.length + 2}
                    style={{ color: '#8a9095', padding: '28px 12px' }}
                  >
                    No {level === 'city' ? 'cities' : 'countries'} match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {visible.length > 400 && (
          <div
            style={{
              padding: '8px 16px',
              fontSize: 11,
              color: '#8a9095',
              borderTop: '1px solid #eef0f0',
            }}
          >
            Showing first 400 of {fmt(visible.length)} - narrow with search.
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#8a9095', marginTop: 8 }}>
        Each cell is <b>ready / published</b> for that product type here.{' '}
        <span style={{ color: RED_FG, fontWeight: 700 }}>Red</span> = short-haul not ready (a real
        gap); <span style={{ color: SKIP_FG, fontWeight: 700 }}>yellow</span> = long-haul not ready
        (parked / skipped, not an error). Click any number to see the list.
        {level === 'city' &&
          ' Visas and insurance are country-only, so they appear under Countries.'}
      </div>
    </div>
  );
};

const GeoDetail = ({
  data,
  level,
  detail,
  colTypes,
  onType,
  onFilter,
  onBack,
}: {
  data: ProductsBreakdownResponse;
  level: GeoLevel;
  detail: { id: string; name: string; sub: string | null; type: ProductType; filter: ListFilter };
  colTypes: ProductType[];
  onType: (t: ProductType) => void;
  onFilter: (f: ListFilter) => void;
  onBack: () => void;
}): React.JSX.Element => {
  const meta = TYPE_META[detail.type];
  const inGeo = (r: ProductCompletenessRow): boolean => geoKey(r, level) === detail.id;

  const all = data[detail.type].filter(inGeo);
  const counts = {
    all: all.length,
    ready: all.filter((r) => isActive(r) && isReady(r, detail.type)).length,
    notready: all.filter((r) => isActive(r) && !isReady(r, detail.type) && !r.isLongHaul).length,
    skipped: all.filter((r) => isActive(r) && !isReady(r, detail.type) && r.isLongHaul).length,
  };
  const perTypeCount = (t: ProductType): number =>
    data[t].filter((r) => geoKey(r, level) === detail.id && isActive(r)).length;

  const list = all.filter((r) => {
    if (detail.filter === 'ready') return isActive(r) && isReady(r, detail.type);
    if (detail.filter === 'notready')
      return isActive(r) && !isReady(r, detail.type) && !r.isLongHaul;
    if (detail.filter === 'skipped') return isActive(r) && !isReady(r, detail.type) && r.isLongHaul;
    return true;
  });

  return (
    <div style={{ marginTop: 16 }}>
      <button
        type="button"
        onClick={onBack}
        className="pd-pill"
        style={{ marginBottom: 12, display: 'inline-flex' }}
      >
        <ArrowLeft size={14} /> Back to {level === 'city' ? 'cities' : 'countries'}
      </button>

      <div className="pd-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #eef0f0' }}>
          <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-.01em' }}>
            {detail.name}
            {detail.sub && (
              <span style={{ fontSize: 13, fontWeight: 500, color: '#8a9095' }}>
                {' '}
                · {detail.sub}
              </span>
            )}
          </div>
          {/* Type switcher */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {colTypes.map((t) => {
              const n = perTypeCount(t);
              return (
                <button
                  key={t}
                  type="button"
                  className={`pd-pill ${detail.type === t ? 'on' : ''}`}
                  onClick={() => onType(t)}
                  disabled={n === 0}
                  style={n === 0 ? { opacity: 0.45, cursor: 'default' } : undefined}
                >
                  {TYPE_META[t].label} <span className="n">{fmt(n)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Ready / not-ready filter */}
        <div
          style={{
            display: 'flex',
            gap: 6,
            padding: '10px 16px',
            borderBottom: '1px solid #eef0f0',
            flexWrap: 'wrap',
          }}
        >
          {(
            [
              ['all', `All (${fmt(counts.all)})`],
              ['ready', `Ready (${fmt(counts.ready)})`],
              ['notready', `Not ready (${fmt(counts.notready)})`],
              ...(counts.skipped > 0
                ? ([['skipped', `Skipped (${fmt(counts.skipped)})`]] as const)
                : []),
            ] as const
          ).map(([k, lbl]) => (
            <button
              key={k}
              type="button"
              className={`pd-pill ${detail.filter === k ? 'on' : ''}`}
              onClick={() => onFilter(k)}
            >
              {lbl}
            </button>
          ))}
        </div>

        <div style={{ maxHeight: 460, overflow: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>{meta.label.replace(/s$/, '')}</th>
                <th>Location</th>
                <th>Status</th>
                <th>{meta.priceLabel}</th>
                {meta.showImage && <th>Image</th>}
                <th>Ready</th>
              </tr>
            </thead>
            <tbody>
              {list.slice(0, 500).map((r) => {
                const ready = isActive(r) && isReady(r, detail.type);
                return (
                  <tr key={r.id}>
                    <td>
                      <Link
                        to={`${meta.route}/${r.id}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {r.title}
                      </Link>
                    </td>
                    <td style={{ textAlign: 'left' }}>
                      <div>
                        {r.cityName ?? r.countryName ?? (
                          <span style={{ color: '#c5c9cb' }}>&ndash;</span>
                        )}
                      </div>
                      {r.cityName && r.countryName && (
                        <div style={{ fontSize: 11, color: '#8a9095' }}>{r.countryName}</div>
                      )}
                    </td>
                    <td>
                      {r.status === 'ACTIVE' ? (
                        <Chip bg="#e7f6ef" fg="#047857">
                          Published
                        </Chip>
                      ) : r.status === 'DRAFT' ? (
                        <Chip bg="#f1f3f4" fg="#6b7075">
                          Draft
                        </Chip>
                      ) : (
                        <Chip bg="#fef3c7" fg="#b45309">
                          {r.status}
                        </Chip>
                      )}
                    </td>
                    <td>
                      {r.priced ? (
                        <span style={{ color: '#047857', fontWeight: 700 }}>&#10003;</span>
                      ) : r.isLongHaul ? (
                        <Chip bg={SKIP_BG} fg={SKIP_FG}>
                          No price
                        </Chip>
                      ) : (
                        <Chip bg={RED_BG} fg={RED_FG}>
                          No price
                        </Chip>
                      )}
                    </td>
                    {meta.showImage && (
                      <td>
                        {r.hasImage ? (
                          <span style={{ color: '#047857', fontWeight: 700 }}>&#10003;</span>
                        ) : r.isLongHaul ? (
                          <Chip bg={SKIP_BG} fg={SKIP_FG}>
                            No image
                          </Chip>
                        ) : (
                          <Chip bg={RED_BG} fg={RED_FG}>
                            No image
                          </Chip>
                        )}
                      </td>
                    )}
                    <td>
                      {ready ? (
                        <Chip bg="#e7f6ef" fg="#047857">
                          Ready
                        </Chip>
                      ) : r.isLongHaul ? (
                        <Chip bg={SKIP_BG} fg={SKIP_FG}>
                          Skipped
                        </Chip>
                      ) : (
                        <Chip bg={RED_BG} fg={RED_FG}>
                          Not ready
                        </Chip>
                      )}
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr>
                  <td
                    colSpan={meta.showImage ? 6 : 5}
                    style={{ color: '#8a9095', padding: '28px 12px' }}
                  >
                    Nothing here for this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {list.length > 500 && (
          <div
            style={{
              padding: '8px 16px',
              fontSize: 11,
              color: '#8a9095',
              borderTop: '1px solid #eef0f0',
            }}
          >
            Showing first 500 of {fmt(list.length)}.
          </div>
        )}
      </div>
    </div>
  );
};
